import React, { useCallback, useEffect, useState } from 'react';
import { FiFolder, FiMail, FiPhone } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import AccountService from '../../services/account.service';
import ContactService from '../../services/contact.service';
import ContractService from '../../services/contract.service';
import AssetService from '../../services/asset.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import HistoryModal from '../../components/HistoryModal';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { EntityList, DetailHeader, TabNav, IdChip, Button } from '../../components/EntityList';
import AccountInfo from '../Accounts/AccountInfo';
import AccountContacts from '../Accounts/AccountContacts';
import AccountDebts from '../Accounts/AccountDebts';
import AccountCredits from '../Accounts/AccountCredits';
import AccountContracts from '../Accounts/AccountContracts';
import AccountAssets from '../Accounts/AccountAssets';
import Balance from '../Balance/Balance';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import { isBalanceEnabled } from '../../utils/featureFlags';

const TAB_SLUGS = ['datos', 'contactos', 'deudas', 'pasividades', 'alquileres', 'activos', 'balance', 'adjuntos'];

const emptyTabState = { rows: [], loading: true, error: '' };

export default function AccountDetail() {
	const { t } = useLabels();
	const { id, tab } = useParams();
	const navigate = useNavigate();
	const { canEdit, canDelete } = usePermissions();

	const [account, setAccount] = useState(null);
	const [loadingAccount, setLoadingAccount] = useState(true);
	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const [contactsState, setContactsState] = useState(emptyTabState);
	const [debtsState, setDebtsState] = useState(emptyTabState);
	const [creditsState, setCreditsState] = useState(emptyTabState);
	const [contractsState, setContractsState] = useState(emptyTabState);
	const [assetsState, setAssetsState] = useState(emptyTabState);

	const fetchAccount = useCallback(async () => {
		setLoadingAccount(true);
		try {
			const result = await AccountService.getDetails(id);
			setAccount(result.data);
		} catch (error) {
			toast.error(getError(error));
		} finally {
			setLoadingAccount(false);
		}
	}, [id]);

	const fetchContacts = useCallback(async () => {
		setContactsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await ContactService.getContactsForAccount(id);
			setContactsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setContactsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	const fetchDebts = useCallback(async () => {
		setDebtsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await AccountService.getDebtsForAccountId(id);
			setDebtsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setDebtsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	const fetchCredits = useCallback(async () => {
		setCreditsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await AccountService.getCreditsForAccountId(id);
			setCreditsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setCreditsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	/**
	 * The account's rentals are the union of two sources: contracts where it is
	 * the tenant, and contracts on assets it owns. Fetched here rather than in
	 * AccountContracts, which is presentational under the refactor.
	 *
	 * Deduped by `_id` because an account can be both sides of the same contract
	 * (it owns the asset and rents it), and each row is tagged with the role(s)
	 * it matched so the list stays readable once merged.
	 */
	const fetchContracts = useCallback(async () => {
		setContractsState((prev) => ({ ...prev, loading: true }));
		try {
			const [tenantRes, assetsRes] = await Promise.all([
				ContractService.getContractsForAccount(id),
				AssetService.listAssetsByAccount(id),
			]);

			const byId = new Map();
			const add = (contract, role) => {
				if (!contract?._id) return;
				const existing = byId.get(contract._id);
				if (existing) {
					if (existing.accountRole !== role) existing.accountRole = 'Inquilino y Propietario';
					return;
				}
				byId.set(contract._id, { ...contract, accountRole: role });
			};

			(tenantRes.data || []).forEach((contract) => add(contract, 'Inquilino'));

			const ownedAssets = assetsRes.data || [];
			const ownedContracts = await Promise.all(
				ownedAssets.map((asset) => ContractService.getContractsForProperty(asset._id)),
			);
			ownedContracts.forEach((res) => (res.data || []).forEach((contract) => add(contract, 'Propietario')));

			setContractsState({ rows: [...byId.values()], loading: false, error: '' });
		} catch (error) {
			setContractsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	const fetchAssets = useCallback(async () => {
		setAssetsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await AssetService.listAssetsByAccount(id);
			setAssetsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setAssetsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	useEffect(() => {
		fetchAccount();
		fetchContacts();
		fetchDebts();
		fetchCredits();
		fetchContracts();
		fetchAssets();
	}, [id]);

	const isEmpresa = account?.type === 'Empresa';
	const currentTab = TAB_SLUGS.includes(tab) && (tab !== 'contactos' || isEmpresa) ? tab : 'datos';

	useEffect(() => {
		if (!loadingAccount && tab !== currentTab) {
			navigate(`/AdminScreen/accounts/account/${id}/${currentTab}`, { replace: true });
		}
	}, [loadingAccount, tab, currentTab, id]);

	function editAccountHandler() {
		navigate(`/AdminScreen/accounts/editAccount/${id}`);
	}

	async function handleConfirmDelete() {
		try {
			await AccountService.delete(id);
			toast.success('Cuenta Eliminada');
			navigate('/AdminScreen/accounts');
		} catch (err) {
			toast.error(getError(err));
		}
	}

	const tabs = [
		{ id: 'datos', label: t('title.details') },
		...(isEmpresa ? [{ id: 'contactos', label: t('entity.contact.plural'), count: contactsState.rows.length }] : []),
		{ id: 'deudas', label: t('entity.debt.plural'), count: debtsState.rows.length },
		{ id: 'pasividades', label: t('entity.credit.plural'), count: creditsState.rows.length },
		{ id: 'alquileres', label: t('entity.contract.plural'), count: contractsState.rows.length },
		{ id: 'activos', label: t('entity.asset.plural'), count: assetsState.rows.length },
		...(isBalanceEnabled() ? [{ id: 'balance', label: t('title.balance') }] : []),
		{ id: 'adjuntos', label: t('title.attachments') },
	];

	const subline = [
		account?.email && { label: 'Email', icon: <FiMail />, value: account.email },
		account?.phone && { label: 'Teléfono', icon: <FiPhone />, value: account.phone },
		account?.folder && { label: 'Carpeta', icon: <FiFolder />, value: account.folder },
	].filter(Boolean);

	return loadingAccount ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.account')} />
		</EntityList>
	) : (
		<EntityList>
			<div className='tw:mb-4 tw:flex tw:items-center tw:gap-1.5 tw:text-[12.5px] tw:text-ink-3'>
				<span className='tw:cursor-pointer tw:hover:text-ink-1' onClick={() => navigate('/AdminScreen/accounts')}>
					{t('entity.account.plural')}
				</span>
				<span>/</span>
				<span className='tw:text-ink-1'>{account?.name}</span>
				{account?.internalId && <IdChip>{account.internalId}</IdChip>}
			</div>

			<DetailHeader
				name={account?.name || ''}
				kicker='Cuenta'
				statusLabel={account?.type}
				statusTone={isEmpresa ? 'accent' : 'neutral'}
				subline={subline}
				actions={
					<>
						<Button onClick={() => setShowHistoryLog(true)}>{t('title.changeHistory')}</Button>
						{canEdit() && (
							<Button variant='primary' onClick={editAccountHandler}>
								{t('action.edit')} cuenta
							</Button>
						)}
						{canDelete() && (
							<Button onClick={() => setShowDeleteModal(true)}>{t('action.delete')}</Button>
						)}
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav
					tabs={tabs}
					current={currentTab}
					onChange={(nextTab) => navigate(`/AdminScreen/accounts/account/${id}/${nextTab}`)}
				/>
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'datos' && <AccountInfo account={account} />}
				{currentTab === 'contactos' && isEmpresa && (
					<AccountContacts
						accountId={id}
						{...contactsState}
						onRefetch={fetchContacts}
					/>
				)}
				{currentTab === 'deudas' && (
					<AccountDebts
						accountId={id}
						{...debtsState}
						onRefetch={fetchDebts}
					/>
				)}
				{currentTab === 'pasividades' && (
					<AccountCredits
						accountId={id}
						{...creditsState}
						onRefetch={fetchCredits}
					/>
				)}
				{currentTab === 'alquileres' && (
					<AccountContracts
						accountId={id}
						{...contractsState}
						onRefetch={fetchContracts}
					/>
				)}
				{currentTab === 'activos' && (
					<AccountAssets
						account={account}
						{...assetsState}
						onRefetch={fetchAssets}
					/>
				)}
				{currentTab === 'balance' && isBalanceEnabled() && <Balance type='account' data={account} />}
				{currentTab === 'adjuntos' && (
					<SingleObjectDocumentManager
						objectId={id}
						objectType='account'
						documentType='account'
						documents={account?.documents}
						object={account}
						onDocumentsChange={fetchAccount}
					/>
				)}
			</div>

			<HistoryModal model='account' show={showHistoryLog} handleClose={() => setShowHistoryLog(false)} id={id} isHistory={true} />

			<ConfirmationModal
				show={showDeleteModal}
				handleClose={() => setShowDeleteModal(false)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteAccount')}
				description={t('msg.confirmDeleteAccount')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>

		</EntityList>
	);
}
